import * as React from "react";
import { Avatar } from "../../../ui/components/Avatar";
import { MockedChip } from "../common/MockedChip";
import type {
  Column,
  EntityTableDefinition,
  RowAccent,
} from "../../../ui/components/Table";
import * as s from "../../../ui/components/Table/Table.css";
import { SmartLinkSyncStatusBadge } from "../../../ui/components/SmartLinkSyncStatusBadge";
import type { AdminUserListItem } from "../../lib/api/admin-users";
import { useI18n } from "../../lib/i18n";

export type UserRowStatus = "verified" | "unverified" | "disabled";

export type UserAuthMethod = "google" | "email";

export function getUserRowStatus(user: AdminUserListItem): UserRowStatus {
  if (user.deletedAt) return "disabled";
  if (user.isEmailVerified) return "verified";
  return "unverified";
}

/** Left-edge accent for the users table (maps to `RowAccent` in Table). */
export function getUserRowAccent(user: AdminUserListItem): RowAccent {
  if (user.deletedAt) return "deleted";
  if (user.isEmailVerified) return "verified";
  return "unverified";
}

export function getUserAuthMethod(user: AdminUserListItem): UserAuthMethod {
  return user.googleId ? "google" : "email";
}

const STATUS_BADGE: Record<
  UserRowStatus,
  { label: string; cls: string; dotCls: string }
> = {
  verified: {
    label: "Email verified",
    cls: s.badgeOnline,
    dotCls: s.dotOnline,
  },
  unverified: {
    label: "Email unverified",
    cls: s.badgePending,
    dotCls: s.dotCommissioning,
  },
  disabled: {
    label: "Disabled",
    cls: s.badgeFault,
    dotCls: s.dotFault,
  },
};

function StatusBadge({ status }: { status: UserRowStatus }) {
  const { label, cls, dotCls } = STATUS_BADGE[status];
  return (
    <span className={`${s.badge} ${cls}`}>
      <span className={`${s.badgeDot} ${dotCls}`} />
      {label}
    </span>
  );
}

function BoolBadge({
  yes,
  yesLabel,
  noLabel,
  yesCls = s.badgeOnline,
  noCls = s.badgePending,
  yesDot = s.dotOnline,
  noDot = s.dotCommissioning,
}: {
  yes: boolean;
  yesLabel: string;
  noLabel: string;
  yesCls?: string;
  noCls?: string;
  yesDot?: string;
  noDot?: string;
}) {
  const label = yes ? yesLabel : noLabel;
  const cls = yes ? yesCls : noCls;
  const dotCls = yes ? yesDot : noDot;
  return (
    <span className={`${s.badge} ${cls}`}>
      <span className={`${s.badgeDot} ${dotCls}`} />
      {label}
    </span>
  );
}

function formatDate(iso?: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function formatPhone(row: AdminUserListItem): string {
  if (!row.phone?.trim()) return "—";
  const phone = row.phone.trim();
  if (row.countryCode?.trim()) {
    return `${row.countryCode.trim()} ${phone}`;
  }
  return phone;
}

const AVATAR_SIZE = 32;

function MembershipRequestedBadge() {
  const { t } = useI18n();
  return (
    <span className={`${s.badge} ${s.badgePending}`}>
      {t("pro.users.membershipRequested")}
    </span>
  );
}

export const USER_TABLE_COLUMNS: Column<AdminUserListItem>[] = [
  {
    id: "avatar",
    label: "Avatar",
    headerLabelIcon: "user",
    width: 52,
    enableHiding: true,
    render: (row) => (
      <Avatar
        email={row.email}
        firstName={row.firstName}
        lastName={row.lastName}
        image={row.avatarUrl ?? undefined}
        size={AVATAR_SIZE}
      />
    ),
  },
  {
    id: "id",
    header: "ID",
    width: 280,
    defaultHidden: true,
    enableHiding: true,
    render: (row) => (
      <span className={s.cellTextSecondaryTabular}>{row.id}</span>
    ),
  },
  {
    id: "name",
    header: "Name",
    width: 220,
    sortable: true,
    sortKey: "name",
    sort: (a, b) =>
      `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`),
    render: (row) => (
      <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span className={s.cellTextPrimary}>
          {row.firstName} {row.lastName}
        </span>
        {(row.pendingMembershipOrgs?.length ?? 0) > 0 && (
          <MembershipRequestedBadge />
        )}
        {row.isMocked && <MockedChip />}
      </span>
    ),
  },
  {
    id: "email",
    header: "Email",
    minWidth: 200,
    sortable: true,
    sortKey: "email",
    sort: (a, b) => a.email.localeCompare(b.email),
    render: (row) => (
      <span className={s.cellTextSecondary}>{row.email}</span>
    ),
  },
  {
    id: "phone",
    header: "Phone",
    width: 150,
    defaultHidden: true,
    render: (row) => (
      <span className={s.cellTextSecondaryTabular}>{formatPhone(row)}</span>
    ),
  },
  {
    id: "phoneVerified",
    header: "Phone verified",
    width: 130,
    defaultHidden: true,
    filterLabel: "Phone verified",
    filterKey: "phone_verified",
    filterType: "checkbox",
    filterOptions: () => [
      { value: "yes", label: "Verified" },
      { value: "no", label: "Not verified" },
    ],
    filterAccessor: (row) => (row.isPhoneVerified ? "yes" : "no"),
    render: (row) => (
      <BoolBadge
        yes={Boolean(row.isPhoneVerified)}
        yesLabel="Verified"
        noLabel="Not verified"
      />
    ),
  },
  {
    id: "profileSetup",
    header: "Profile setup",
    width: 130,
    defaultHidden: true,
    filterLabel: "Profile setup",
    filterKey: "profile_setup",
    filterType: "checkbox",
    filterOptions: () => [
      { value: "complete", label: "Complete" },
      { value: "pending", label: "Pending" },
    ],
    filterAccessor: (row) =>
      row.profileSetupCompleted ? "complete" : "pending",
    render: (row) => (
      <BoolBadge
        yes={Boolean(row.profileSetupCompleted)}
        yesLabel="Complete"
        noLabel="Pending"
      />
    ),
  },
  {
    id: "authMethod",
    header: "Sign-in",
    width: 110,
    defaultHidden: true,
    filterLabel: "Sign-in method",
    filterKey: "auth_method",
    filterType: "checkbox",
    filterOptions: () => [
      { value: "google", label: "Google" },
      { value: "email", label: "Email" },
    ],
    filterAccessor: (row) => getUserAuthMethod(row),
    render: (row) => (
      <span className={s.cellTextSecondary}>
        {getUserAuthMethod(row) === "google" ? "Google" : "Email"}
      </span>
    ),
  },
  {
    id: "locked",
    header: "Locked",
    width: 100,
    defaultHidden: true,
    filterLabel: "Account locked",
    filterKey: "locked",
    filterType: "checkbox",
    filterOptions: () => [
      { value: "yes", label: "Locked" },
      { value: "no", label: "Not locked" },
    ],
    filterAccessor: (row) => (row.lockedAt ? "yes" : "no"),
    render: (row) => (
      <BoolBadge
        yes={Boolean(row.lockedAt)}
        yesLabel="Locked"
        noLabel="—"
        yesCls={s.badgeFault}
        noCls={s.badgeNone}
        yesDot={s.dotFault}
        noDot={s.dotStandby}
      />
    ),
  },
  {
    id: "createdAt",
    header: "Created",
    width: 140,
    sortable: true,
    sortKey: "created_at",
    defaultHidden: true,
    render: (row) => (
      <span className={s.cellTextSecondaryTabular}>
        {formatDate(row.createdAt)}
      </span>
    ),
  },
  {
    id: "updatedAt",
    header: "Updated",
    width: 140,
    sortable: true,
    sortKey: "updated_at",
    defaultHidden: true,
    render: (row) => (
      <span className={s.cellTextSecondaryTabular}>
        {formatDate(row.updatedAt)}
      </span>
    ),
  },
  {
    id: "organizations",
    header: "Organizations",
    minWidth: 160,
    defaultHidden: false,
    filterKey: "organization_ids",
    filterLabel: "Organizations",
    showFilterByDefault: false,
    filterType: "checkbox",
    filterOptions: () => [],
    filterAccessor: (row) =>
      (row.organizations ?? []).map((org) => org.id),
    render: (row) => {
      const names = (row.organizations ?? []).map((o) => o.name);
      if (names.length === 0) return <span className={s.cellTextSecondary}>—</span>;
      const label =
        names.length <= 2
          ? names.join(", ")
          : `${names.slice(0, 2).join(", ")}, +${names.length - 2}`;
      return (
        <span className={s.cellTextSecondary} title={names.join(", ")}>
          {label}
        </span>
      );
    },
  },
  {
    // Display-only (no server-side filter support for this column yet) —
    // deliberately not wired to filterKey/filterOptions like the columns
    // above, which round-trip through listUsers()'s query.filters.
    id: "smartLinkSyncStatus",
    header: "Smart Link",
    width: 130,
    render: (row) =>
      row.smartLinkSyncStatus ? (
        <SmartLinkSyncStatusBadge status={row.smartLinkSyncStatus} />
      ) : (
        <span className={s.cellTextSecondary}>—</span>
      ),
  },
  {
    id: "status",
    label: "Email status",
    header: "Email status",
    width: 150,
    sortable: true,
    sortKey: "status",
    filterKey: "status",
    filterLabel: "Email status",
    showFilterByDefault: true,
    sort: (a, b) => getUserRowStatus(a).localeCompare(getUserRowStatus(b)),
    filterType: "checkbox",
    filterOptions: () => [
      { value: "verified", label: "Email verified" },
      { value: "unverified", label: "Email unverified" },
      { value: "disabled", label: "Disabled" },
    ],
    filterAccessor: (row) => getUserRowStatus(row),
    render: (row) => <StatusBadge status={getUserRowStatus(row)} />,
  },
];

export function buildUsersTableColumns(
  orgFilterOptions: Array<{ value: string; label: string }>,
): Column<AdminUserListItem>[] {
  return USER_TABLE_COLUMNS.map((col) =>
    col.id === "organizations"
      ? {
          ...col,
          filterOptions: () => orgFilterOptions,
        }
      : col,
  );
}

/** Shared table metadata (columns + row accent). Bulk actions are wired in `UsersListPage`. */
export const USERS_TABLE_DEFINITION: Pick<
  EntityTableDefinition<AdminUserListItem>,
  "columns" | "getRowAccent"
> = {
  columns: USER_TABLE_COLUMNS,
  getRowAccent: getUserRowAccent,
};
